<template>
  <section
    ref="rootElement"
    :class="{
      'two-columns-text-and-three-orbiting-images': true,
    }"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100"
      height="100"
      circle-radius="140"
      style="visibility: hidden; position: fixed"
      version="1.1"
    >
      <!-- First arc -->
      <path
        arc-number="0"
        d="M 0 0 A 140 140 0 0 1 242.4871130596428  0"
        fill="none"
        stroke="red"
        stroke-width="2"
      ></path>
      <!-- Second arc -->
      <path
        arc-number="1"
        d="M 0 0 A 140 140 0 0 1 -121.2435565298214 210"
        fill="none"
        stroke="green"
        stroke-width="2"
      ></path>
      <!-- Third arc -->
      <path
        arc-number="2"
        d="M 0 0 A 140 140 0 0 1 -121.2435565298214 -210"
        fill="none"
        stroke="blue"
        stroke-width="2"
      ></path>
    </svg>
    <GeneralTheme :theme="configuration">
      <div :class="{ 'homepage-container mx-auto': !fullWidthContent }">
        <div
          :class="{
            'grid grid-cols-12 items-center gap-3': true,
          }"
        >
          <div :class="primaryColumnClasses">
            <BlockTheme :theme="contentBlockTheme">
              <SectionBuilder
                v-if="content"
                v-bind="content"
              />
            </BlockTheme>
          </div>
          <div :class="secondaryColumnClasses">
            <Background :theme="backgroundTheme">
              <div
                :class="{
                  'circles-container three-orbiting relative min-h-[450px] overflow-hidden md:min-h-[500px]': true,
                }"
              >
                <div
                  :class="{
                    'three-orbiting-images': true,
                  }"
                  radius="150"
                >
                  <div
                    v-for="(image, index) in imageBlock"
                    :key="index"
                    :class="{
                      'rotated-circle image-animate absolute': true,
                    }"
                  >
                    <PtcPicture
                      v-if="image && image.asset"
                      provider="sanity"
                      format="webp"
                      :src="image.asset._ref"
                      alt="altText"
                      :img-attrs="{
                        class: { 'w-[170px] md:w-[250px]': true },
                      }"
                      :modifiers="{
                        crop: image.crop,
                        hotspot: image.hotspot,
                      }"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </Background>
          </div>
        </div>
      </div>
    </GeneralTheme>
  </section>
</template>

<script setup lang="ts">
import type { SectionBuilderSpinningTwoColumn } from '~/sanity/types';
import OrbitingImage from '../Themes/OrbitingImage';
import PtcPicture from '~/components/Atmos/PtcPicture/PtcPicture.vue';
import Background from '~/components/Atmos/Background/Background.vue';
import BlockTheme from '../Themes/BlockTheme.vue';
import GeneralTheme from '../Themes/GeneralTheme.vue';
import SectionBuilder from '~/components/Atmos/SectionBuilder/SectionBuilder.vue';

const props = defineProps<SectionBuilderSpinningTwoColumn>();
const { content, imageBlock, alignment } = props.spinningTwoColumn ?? {};
const primaryAlignment = ref<string>(alignment || 'left'); // Fallback to default if no width is provided

const primaryColumnClasses = computed(() => ({
  [alignmentStyles[primaryAlignment.value] ?? '']: true,
  'content-center col-span-12 lg:col-span-6 text-section': true,
}));
const secondaryColumnClasses = computed(() => ({
  [alignmentSecodaryStyles[primaryAlignment.value] ?? '']: true,
  'content-center col-span-12 lg:col-span-6 images-section': true,
}));

const alignmentStyles: Record<string, string> = {
  left: 'lg:order-0',
  right: 'lg:order-1',
};
const alignmentSecodaryStyles: Record<string, string> = {
  left: 'lg:order-1',
  right: 'lg:order-0',
};
const rootElement = useTemplateRef('rootElement');
onMounted(() => {
  if (rootElement.value) {
    const orbitingImage = new OrbitingImage(rootElement.value);
    orbitingImage.run();
  }
});
</script>

<style lang="scss">
.two-columns-text-and-three-orbiting-images {
  .quote-section {
    margin-left: -85px;

    .quote-mark {
      font-family: Raleway;
      font-size: 70px;
      font-weight: 900;
      line-height: 82px;
      letter-spacing: 0em;
      color: var(--www-color-primary-light);
      margin: auto;
      text-align: center;
    }

    .quote-text {
      font-family: Raleway;
      font-size: 20px;
      font-style: italic;
      font-weight: 400;
      line-height: 23px;
      letter-spacing: 0em;
      text-align: left;
      position: relative;

      span {
        padding: 0px 30px;
        display: block;
      }

      &::before {
        content: '';
        width: 100%;
        height: 50px;
        display: block;
        border-top: 1px solid var(--www-color-neutral-medium-light);
        border-left: 1px solid var(--www-color-neutral-medium-light);
        position: absolute;
        z-index: -1;
      }
    }

    .quote-person {
      font-family: Raleway;
      font-size: 20px;
      font-style: italic;
      font-weight: 400;
      line-height: 23px;
      letter-spacing: 0em;
      text-align: right;
      position: relative;

      span {
        display: inline;
      }

      .wrapper {
        padding: 10px 30px;
        width: max-content;
        margin-left: auto;
        position: relative;

        .green {
          font-family: Raleway;
          font-size: 20px;
          font-weight: 400;
          line-height: 23px;
          letter-spacing: 0em;
          text-align: left;
          color: var(--www-color-primary-light);
        }

        .bold {
          font-family: Raleway;
          font-size: 20px;
          font-weight: 600;
          line-height: 23px;
          letter-spacing: 0em;
          text-align: left;
        }

        &:after {
          content: '';
          position: absolute;
          width: 18px;
          height: 5px;
          background-color: var(--www-color-primary-light);
          bottom: 0;
          left: 0;
        }
      }

      &::before,
      &::after {
        content: '';
        display: block;
        position: absolute;
        border-bottom: 1px solid;
        border-right: 1px solid;
      }

      &::before {
        height: 70px;
        width: 80%;
        z-index: -1;
        right: 0px;
        bottom: -25px;
        border-color: var(--www-color-primary-pale);
      }

      &::after {
        width: 100%;
        height: 50px;
        border-color: var(--www-color-neutral-medium-light);
        z-index: -2;
        right: -7px;
        bottom: -19px;
      }
    }
  }
}

@media (max-width: 768px) {
  .two-columns-text-and-three-orbiting-images {
    .text-section {
      order: 2;
      margin-top: 60px;
    }

    .images-section {
      order: 1;
    }
  }
}

@media (max-width: 992px) {
  .two-columns-text-and-three-orbiting-images {
    .quote-section {
      margin-left: 0px;
      max-width: 100%;
      .quote-text {
        &::before {
          border-left: 0;
        }
      }

      .quote-person {
        .wrapper {
          justify-content: center !important;
          max-width: 100%;
          flex-wrap: wrap;

          &:after {
            bottom: 50%;
            transform: translateY(50%);
          }
        }

        &::before {
          display: none;
        }

        &::after {
          border-color: var(--www-color-primary-pale);
          border-right: 0;
          right: 0px;
        }
      }
    }
  }
}
</style>
